
import { motion } from 'framer-motion';
import { vehicleTypes } from '../../../mocks/pricingData';

export default function VehicleComparison() {
  const features = [
    { label: 'Passagers max.', berline: '3', van: '7', icon: 'ri-user-line' },
    { label: 'Bagages max.', berline: '3', van: '8', icon: 'ri-suitcase-line' },
    { label: 'Tarif horaire', berline: '85 €/h', van: '120 €/h', icon: 'ri-time-line' },
    { label: 'WiFi gratuit', berline: true, van: true, icon: 'ri-wifi-line' },
    { label: 'Eau & rafraîchissements', berline: true, van: true, icon: 'ri-goblet-line' },
    { label: 'Chargeurs USB', berline: true, van: true, icon: 'ri-battery-charge-line' },
    { label: 'Sièges cuir', berline: true, van: true, icon: 'ri-armchair-line' },
    { label: 'Climatisation', berline: true, van: true, icon: 'ri-temp-cold-line' },
    { label: 'Espace skis', berline: 'Limité', van: 'Large', icon: 'ri-snowflake-line' },
    { label: 'Idéal pour', berline: 'Couples & affaires', van: 'Familles & groupes', icon: 'ri-heart-line' },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-voyage-gold/10 rounded-full mb-4">
            <i className="ri-car-line text-voyage-gold"></i>
            <span className="font-inter text-sm font-semibold text-voyage-gold">Nos véhicules</span>
          </div>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-night-blue mb-3">
            Comparatif des véhicules
          </h2>
          <p className="font-inter text-slate-600 max-w-2xl mx-auto">
            Choisissez le véhicule adapté à vos besoins pour un confort optimal
          </p>
        </motion.div>

        {/* Cartes véhicules en haut */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {vehicleTypes.map((v, idx) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-100"
            >
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={v.image}
                  alt={`${v.name} - ${v.type}`}
                  title={`${v.name} transfert premium Alpes`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="font-playfair text-2xl font-bold text-night-blue">{v.name}</h3>
                <p className="font-inter text-slate-500 text-sm mt-1">{v.type}</p>
                <div className="mt-4 flex items-center justify-center gap-6">
                  <span className="flex items-center gap-1.5 font-inter text-sm text-night-blue">
                    <i className="ri-user-line text-voyage-gold"></i> {v.passengers} pers.
                  </span>
                  <span className="flex items-center gap-1.5 font-inter text-sm text-night-blue">
                    <i className="ri-suitcase-line text-voyage-gold"></i> {v.luggage} bag.
                  </span>
                </div>
                <div className="mt-4">
                  <span className="font-playfair text-3xl font-bold text-night-blue">{v.hourlyRate} €</span>
                  <span className="font-inter text-sm text-slate-500">/heure</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tableau comparatif */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden"
        >
          <div className="grid grid-cols-3">
            <div className="p-4 bg-slate-50 border-b border-slate-100">
              <span className="font-inter text-sm font-semibold text-slate-500">Caractéristique</span>
            </div>
            <div className="p-4 bg-slate-50 border-b border-slate-100 text-center">
              <span className="font-inter text-sm font-bold text-night-blue">Berline</span>
            </div>
            <div className="p-4 bg-slate-50 border-b border-slate-100 text-center">
              <span className="font-inter text-sm font-bold text-night-blue">Van</span>
            </div>
          </div>

          {features.map((f, idx) => (
            <div key={f.label} className={`grid grid-cols-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
              <div className="p-4 flex items-center gap-2 border-b border-slate-100">
                <i className={`${f.icon} text-voyage-gold text-base`}></i>
                <span className="font-inter text-sm text-night-blue">{f.label}</span>
              </div>
              <div className="p-4 text-center border-b border-slate-100">
                {typeof f.berline === 'boolean' ? (
                  f.berline ? (
                    <i className="ri-check-line text-green-500 text-lg"></i>
                  ) : (
                    <i className="ri-close-line text-red-400 text-lg"></i>
                  )
                ) : (
                  <span className="font-inter text-sm font-medium text-night-blue">{f.berline}</span>
                )}
              </div>
              <div className="p-4 text-center border-b border-slate-100">
                {typeof f.van === 'boolean' ? (
                  f.van ? (
                    <i className="ri-check-line text-green-500 text-lg"></i>
                  ) : (
                    <i className="ri-close-line text-red-400 text-lg"></i>
                  )
                ) : (
                  <span className="font-inter text-sm font-medium text-night-blue">{f.van}</span>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
